/**
 * @file ResetPasswordPage.tsx
 * @description Public page reached from the reset email link with `?token=`.
 *   User enters a new password (twice). On success, navigates to /login
 *   so they can sign in with the new credentials.
 * @module Pages
 * @security Token is single-use server-side. The page sends it once and
 *   doesn't persist it locally beyond the URL.
 */

import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/common/AuthLayout';
import { Button } from '../components/common/Button';
import { PasswordField } from '../components/common/PasswordField';
import { confirmPasswordReset } from '../lib/auth-api';
import { ApiError } from '../lib/api';
import { isPasswordBreachedError } from '../lib/form-helpers';

interface Errors {
  next?: string;
  confirm?: string;
  form?: string;
}

function ResetPasswordForm({ token }: { token: string }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);

  if (token === '') {
    return (
      <div className="w-full max-w-sm text-center space-y-3">
        <h1 className="text-xl font-semibold text-slate-900">{t('reset.missing_token_title')}</h1>
        <p className="text-sm text-slate-500">{t('reset.missing_token_body')}</p>
        <Link to="/forgot-password" className="text-sm text-brand-strong hover:underline">
          {t('reset.request_again')}
        </Link>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const v: Errors = {};
    if (next.length < 12) v.next = t('login.password_too_short');
    if (next !== confirm) v.confirm = t('settings.confirm_mismatch');
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await confirmPasswordReset(token, next);
      navigate('/login');
    } catch (err) {
      if (isPasswordBreachedError(err)) {
        setErrors({ form: t('reset.password_breached') });
      } else if (err instanceof ApiError && err.status === 400) {
        setErrors({ form: t('reset.invalid_or_expired') });
      } else {
        setErrors({ form: t('reset.generic_error') });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4" noValidate>
      <h1 className="text-2xl font-bold text-slate-900">{t('reset.title')}</h1>
      <p className="text-sm text-slate-500">{t('reset.subtitle')}</p>
      {errors.form !== undefined ? (
        <p role="alert" className="text-sm text-red-600">
          {errors.form}
        </p>
      ) : null}
      <PasswordField
        id="next"
        label={t('settings.new_label')}
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        error={errors.next}
        required
      />
      <PasswordField
        id="confirm"
        label={t('settings.confirm_label')}
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        error={errors.confirm}
        required
      />
      <Button type="submit" loading={loading} className="w-full">
        {t('reset.submit')}
      </Button>
    </form>
  );
}

export default function ResetPasswordPage(): JSX.Element {
  const [search] = useSearchParams();
  const token = search.get('token') ?? '';
  return (
    <AuthLayout>
      <ResetPasswordForm token={token} />
    </AuthLayout>
  );
}
